import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { offeringsApi } from '@/rest/modules/offerings-api';
import { CreateOfferingAgePricingDto, OfferingAgePricing, UpdateOfferingAgePricingDto } from '@/domain/offerings/offering';
import { useToast } from '@/hooks/use-toast';

export const useOfferingAgePricing = (offeringId: string) => {
  return useQuery<OfferingAgePricing | null>({
    queryKey: ['offering-age-pricing', offeringId],
    queryFn: () => offeringsApi.getAgePricing(offeringId),
    enabled: !!offeringId,
  });
};

export const useOfferingAgePricingByPrice = (offeringId: string, priceId: string | null) => {
  return useQuery<OfferingAgePricing | null>({
    queryKey: ['offering-age-pricing-history', priceId],
    queryFn: () => (offeringId && priceId) ? offeringsApi.getAgePricingByPrice(offeringId, priceId) : Promise.resolve(null),
    enabled: !!offeringId && !!priceId,
  });
};

export const useAddAgePricing = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ offeringId, priceId, data }: { offeringId: string; priceId: string; data: CreateOfferingAgePricingDto }) => 
      offeringsApi.addAgePricing(offeringId, priceId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['offering-age-pricing', variables.offeringId] });
      queryClient.invalidateQueries({ queryKey: ['offering-age-pricing-history', variables.priceId] });
      queryClient.refetchQueries({ queryKey: ['offering', variables.offeringId, { includePrices: true }] });
      toast({
        title: 'Éxito',
        description: 'Precios por edad actualizados correctamente',
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Error al actualizar los precios por edad',
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateAgePricing = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ offeringId, priceId, data }: { offeringId: string; priceId: string; data: UpdateOfferingAgePricingDto }) => 
      offeringsApi.updateAgePricing(offeringId, priceId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['offering-age-pricing', variables.offeringId] });
      queryClient.invalidateQueries({ queryKey: ['offering-age-pricing-history', variables.priceId] });
      toast({
        title: 'Éxito',
        description: 'Precios por edad actualizados correctamente',
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Error al actualizar los precios por edad',
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteAgePricing = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ offeringId, priceId }: { offeringId: string; priceId: string }) => 
      offeringsApi.deleteAgePricing(offeringId, priceId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['offering-age-pricing', variables.offeringId] });
      queryClient.invalidateQueries({ queryKey: ['offering-age-pricing-history', variables.priceId] });
      toast({
        title: 'Éxito',
        description: 'Precios por edad eliminados correctamente',
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Error al eliminar los precios por edad',
        variant: 'destructive',
      });
    },
  });
};
